import type { ReactNode } from 'react';
import { viewToggleButtonActive, viewToggleButtonInactive, viewToggleContainer } from '@/lib/page-card-styles';
import { cn } from '@/lib/utils';

export type SegmentedToggleOption<T extends string> = {
  value: T;
  label: ReactNode;
  icon?: ReactNode;
  disabled?: boolean;
};

type SegmentedToggleProps<T extends string> = {
  value: T;
  options: readonly SegmentedToggleOption<T>[];
  onChange: (value: T) => void;
  className?: string;
  /** Compact height for dense mobile toolbars (matches sm date/search controls). */
  size?: 'default' | 'sm';
  /** Use when the control is purely presentational; otherwise defaults to "tablist". */
  ariaRole?: 'tablist' | 'radiogroup';
  ariaLabel?: string;
};

export function SegmentedToggle<T extends string>({
  value,
  options,
  onChange,
  className,
  size = 'default',
  ariaRole = 'tablist',
  ariaLabel,
}: SegmentedToggleProps<T>) {
  const isSm = size === 'sm';

  return (
    <div className={cn(viewToggleContainer, isSm && 'h-8', className)} role={ariaRole} aria-label={ariaLabel}>
      {options.map((opt, idx) => {
        const isActive = opt.value === value;
        const isFirst = idx === 0;
        const isLast = idx === options.length - 1;
        return (
          <button
            key={opt.value}
            type='button'
            role={ariaRole === 'tablist' ? 'tab' : undefined}
            aria-selected={ariaRole === 'tablist' ? isActive : undefined}
            disabled={opt.disabled}
            onClick={() => onChange(opt.value)}
            className={cn(
              // min-w-max: keep full labels readable; container scrolls on narrow viewports
              'flex flex-1 min-w-max items-center justify-center transition-colors duration-150 whitespace-nowrap',
              isActive ? 'font-semibold' : 'font-medium',
              isSm ? 'h-8 gap-1.5 px-2 text-xs [&_svg]:h-3.5 [&_svg]:w-3.5' : 'h-10 gap-2 px-3 sm:px-4 text-sm',
              isActive ? viewToggleButtonActive : viewToggleButtonInactive,
              isFirst ? 'rounded-l-md rounded-r-none' : isLast ? 'rounded-r-md rounded-l-none' : 'rounded-none',
              !isLast && 'border-r border-gray-200/70 dark:border-gray-600/70',
              opt.disabled && 'opacity-60 cursor-not-allowed',
            )}
          >
            {opt.icon}
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}
